<template>
    <v-container fluid class="admin-page">
        <!-- Header -->
        <div class="page-header">
            <div>
                <p class="page-kicker">Back-office</p>
                <h1 class="page-title">Détail de la demande</h1>
                <p class="page-subtitle" v-if="demande && demande.reference_demande">
                    Référence : {{ demande.reference_demande }}
                </p>
            </div>
            <v-btn variant="outlined" class="action-button" @click="$router.push({ name: 'DemandeAlumniIndex' })">
                <v-icon start>mdi-arrow-left</v-icon> Retour
            </v-btn>
        </div>

        <!-- Loader -->
        <v-skeleton-loader v-if="isLoading && !demande" class="mx-auto" type="article, article"></v-skeleton-loader>

        <template v-if="demande">
            <!-- Profil -->
            <v-card variant="outlined" class="section-card profile-card">
                <div class="profile-header">
                    <v-avatar color="#e0e7ff" size="64">
                        <span class="profile-initials">{{ demande.prenom[0] }}{{ demande.nom[0] }}</span>
                    </v-avatar>
                    <div class="profile-identity">
                        <h2 class="profile-name">{{ civilite }} {{ demande.prenom }} {{ demande.nom }}</h2>
                        <p class="profile-birth">
                            Né(e) le {{ $longDateFormat(demande.date_de_naissance) }} à
                            {{ demande.lieu_de_naissance }}
                        </p>
                        <v-chip theme="dark" size="small" :color="$colorDemandeIndiv(demande.step)" class="mt-2">
                            <v-icon start size="small">{{ $iconDemandeIndiv(demande.step) }}</v-icon>
                            {{ $statutDemandeIndiv(demande.step) }}
                        </v-chip>
                    </div>
                    <v-spacer></v-spacer>
                    <div class="profile-dates">
                        <div class="info-item">
                            <p class="info-label">Date d'enregistrement</p>
                            <p class="info-value">{{ $longDateFormat(demande.created_at) }}</p>
                        </div>
                        <div class="info-item" v-if="demande.appointment_date">
                            <p class="info-label">Date de retrait prévue</p>
                            <p class="info-value">{{ $longDateFormat(demande.appointment_date) }}</p>
                        </div>
                        <div class="info-item" v-if="demande.delivered_date">
                            <p class="info-label">Date de retrait</p>
                            <p class="info-value">{{ $longDateFormat(demande.delivered_date) }}</p>
                        </div>
                    </div>
                </div>
            </v-card>

            <div class="my-4"></div>

            <!-- Informations personnelles -->
            <v-card variant="outlined" class="section-card">
                <div class="section-card-header">
                    <v-avatar size="34" color="#e0e7ff" class="section-icon">
                        <v-icon color="#4f46e5">mdi-account-outline</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Informations personnelles</h2>
                </div>
                <v-divider></v-divider>
                <div class="section-card-body">
                    <v-row>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Année d'entrée à l'UCAD</p>
                            <p class="info-value">{{ demande.annee_entree_ucad }}</p>
                        </v-col>
                        <v-col v-if="demande.annee_sortie_ucad" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Année de sortie de l'UCAD</p>
                            <p class="info-value">{{ demande.annee_sortie_ucad }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Établissement(s) fréquenté(s)</p>
                            <p class="info-value">
                                <span v-for="(fac, index) of demande.faculte_frequente" :key="fac.id">
                                    - {{ fac.nom }}
                                    <br v-if="index < demande.faculte_frequente.length - 1" />
                                </span>
                            </p>
                        </v-col>
                    </v-row>
                </div>
            </v-card>

            <div class="my-4"></div>

            <!-- Diplôme -->
            <v-card variant="outlined" class="section-card">
                <div class="section-card-header">
                    <v-avatar size="34" color="#dcfce7" class="section-icon">
                        <v-icon color="#059669">mdi-school-outline</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Diplôme</h2>
                </div>
                <v-divider></v-divider>
                <div class="section-card-body">
                    <v-row>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Numéro carte d'étudiant</p>
                            <p class="info-value">{{ demande.num_carte || 'Non renseigné' }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Diplôme demandé</p>
                            <p class="info-value">{{ demande.dernier_diplome_obtenu }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Mention du diplôme</p>
                            <p class="info-value">{{ demande.mention_diplome }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Établissement d'obtention</p>
                            <p class="info-value">{{ demande.etab_diplome ? demande.etab_diplome.nom : 'Non renseigné' }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Année d'obtention</p>
                            <p class="info-value">{{ demande.annee_obtention }}</p>
                        </v-col>
                        <v-col v-if="demande.grade_diplome" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Grade du diplôme</p>
                            <p class="info-value">{{ demande.grade_diplome.libelle_long }}</p>
                        </v-col>
                        <v-col v-if="demande.autre_grade" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Autre grade</p>
                            <p class="info-value">{{ demande.autre_grade }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Diplôme déjà délivré ?</p>
                            <p class="info-value">{{ demande.diplome_delivre ? 'Oui' : 'Non' }}</p>
                        </v-col>
                        <v-col v-if="demande.diplome_delivre" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Date de délivrance</p>
                            <p class="info-value">{{ $longDateFormat(demande.date_delivrance) }}</p>
                        </v-col>
                        <v-col v-if="demande.motif_rejet" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Motif du rejet</p>
                            <p class="info-value">{{ demande.motif_rejet }}</p>
                        </v-col>
                    </v-row>
                </div>
            </v-card>

            <div class="my-4"></div>

            <!-- Attestation -->
            <v-card variant="outlined" class="section-card">
                <div class="section-card-header">
                    <v-avatar size="34" color="#fef3c7" class="section-icon">
                        <v-icon color="#d97706">mdi-file-document-outline</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Attestation de réussite</h2>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="attestationUrl"
                        variant="outlined"
                        size="small"
                        class="action-button"
                        :href="attestationUrl"
                        target="_blank"
                    >
                        <v-icon start size="small">mdi-open-in-new</v-icon> Ouvrir dans un nouvel onglet
                    </v-btn>
                </div>
                <v-divider></v-divider>
                <div class="section-card-body">
                    <template v-if="attestationUrl">
                        <v-img
                            v-if="attestationIsImage"
                            :src="attestationUrl"
                            contain
                            max-height="800"
                            class="attestation-frame"
                        ></v-img>
                        <iframe
                            v-else
                            width="100%"
                            height="800px"
                            :src="attestationUrl + '#zoom=100'"
                            frameborder="0"
                            class="attestation-frame"
                        ></iframe>
                    </template>
                    <p v-else class="info-value">Aucune attestation fournie.</p>
                </div>
            </v-card>

            <div class="my-4"></div>

            <!-- Contacts -->
            <v-card variant="outlined" class="section-card">
                <div class="section-card-header">
                    <v-avatar size="34" color="#dbeafe" class="section-icon">
                        <v-icon color="#2563eb">mdi-phone-outline</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Contacts</h2>
                </div>
                <v-divider></v-divider>
                <div class="section-card-body">
                    <v-row>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Téléphone 1</p>
                            <p class="info-value">{{ demande.telephone_1 }}</p>
                        </v-col>
                        <v-col v-if="demande.telephone_2" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Téléphone 2</p>
                            <p class="info-value">{{ demande.telephone_2 }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Email</p>
                            <p class="info-value">{{ demande.email }}</p>
                        </v-col>
                        <v-col v-if="demande.boite_postale" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Boite postale</p>
                            <p class="info-value">{{ demande.boite_postale }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Ville</p>
                            <p class="info-value">{{ demande.ville }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Pays</p>
                            <p class="info-value">{{ demande.pays }}</p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Adresse</p>
                            <p class="info-value">{{ demande.adresse }}</p>
                        </v-col>
                    </v-row>
                </div>
            </v-card>

            <div class="my-4"></div>

            <!-- Situation professionnelle -->
            <v-card variant="outlined" class="section-card">
                <div class="section-card-header">
                    <v-avatar size="34" color="#f3e8ff" class="section-icon">
                        <v-icon color="#9333ea">mdi-briefcase-outline</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Situation professionnelle</h2>
                </div>
                <v-divider></v-divider>
                <div class="section-card-body">
                    <v-row>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Situation actuelle</p>
                            <p class="info-value">{{ demande.situation_actuelle.replaceAll('_', ' ') }}</p>
                        </v-col>
                        <v-col v-if="demande.structure" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Structure</p>
                            <p class="info-value">{{ demande.structure }}</p>
                        </v-col>
                        <v-col v-if="demande.autre_situation" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Autre situation</p>
                            <p class="info-value">{{ demande.autre_situation }}</p>
                        </v-col>
                        <v-col v-if="demande.secteur_activite" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Secteur d'activité</p>
                            <p class="info-value">{{ demande.secteur_activite }}</p>
                        </v-col>
                        <v-col v-if="demande.poste_occupe" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Poste occupé</p>
                            <p class="info-value">{{ demande.poste_occupe }}</p>
                        </v-col>
                        <v-col v-if="demande.adresse_professionnelle" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Adresse professionnelle</p>
                            <p class="info-value">{{ demande.adresse_professionnelle }}</p>
                        </v-col>
                        <v-col v-if="demande.telephone_professionnelle" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Téléphone professionnel</p>
                            <p class="info-value">{{ demande.telephone_professionnelle }}</p>
                        </v-col>
                        <v-col v-if="demande.email_professionnelle" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Email professionnel</p>
                            <p class="info-value">{{ demande.email_professionnelle }}</p>
                        </v-col>
                        <v-col v-if="demande.boite_postale_professionnelle" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Boite postale professionnelle</p>
                            <p class="info-value">{{ demande.boite_postale_professionnelle }}</p>
                        </v-col>
                        <v-col v-if="demande.site_web" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Site web</p>
                            <p class="info-value">
                                <a :href="demande.site_web" target="_blank">{{ demande.site_web }}</a>
                            </p>
                        </v-col>
                    </v-row>
                </div>
            </v-card>

            <div class="my-4"></div>

            <!-- Informations complémentaires -->
            <v-card variant="outlined" class="section-card">
                <div class="section-card-header">
                    <v-avatar size="34" color="#f1f5f9" class="section-icon">
                        <v-icon color="#475569">mdi-information-outline</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Informations complémentaires</h2>
                </div>
                <v-divider></v-divider>
                <div class="section-card-body">
                    <v-row>
                        <v-col cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Lien maintenu avec l'UCAD ?</p>
                            <p class="info-value">{{ demande.laison_ucad ? 'Oui' : 'Non' }}</p>
                        </v-col>
                        <v-col v-if="!demande.laison_ucad && demande.raison_non_laison" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Raison du détachement</p>
                            <p class="info-value">{{ demande.raison_non_laison }}</p>
                        </v-col>
                        <v-col v-if="demande.domain_contribution" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Domaine(s) de contribution</p>
                            <p class="info-value">
                                <span
                                    v-for="(domain, index) of demande.domain_contribution.split(';')"
                                    :key="index"
                                    class="text-capitalize"
                                >
                                    - {{ domain }}
                                    <br v-if="index < demande.domain_contribution.split(';').length - 1" />
                                </span>
                            </p>
                        </v-col>
                        <v-col v-if="demande.other_domains && demande.other_domains.length > 0" cols="12" sm="6" md="4" class="info-item">
                            <p class="info-label">Autres domaines</p>
                            <p class="info-value">{{ demande.other_domains }}</p>
                        </v-col>
                    </v-row>
                </div>
            </v-card>
        </template>

        <demande-indiv-action-dialog
            v-if="demande"
            :show="showActionDialog"
            :step="step2go"
            :demandeId="demande.id"
            :isLoading="isLoading"
            @done="done"
            @handleLoading="(val) => (isLoading = val)"
            @cancel="cancel"
        ></demande-indiv-action-dialog>
        <v-overlay :model-value="isLoading" class="align-center justify-center" :z-index="204">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-container>
</template>

<script lang="js">
import { getOneIndivDemande } from "../../../functions/request_utils/getOneIndivDemande";
import DemandeIndivActionDialog from "../../../components/shared/DemandeIndivActionDialog.vue"
export default {
    name: 'DemandeIndivShow',
    components: { DemandeIndivActionDialog },
    data: () => ({
        demande: null,
        isLoading: false,
        showActionDialog: false,
        step2go: ''
    }),
    created() {
        this.fetchIndivDemande()
    },
    computed: {
        alumniId() {
            return this.$route.params.id
        },
        civilite() {
            if (this.demande && this.demande.civilite) {
                return this.demande.civilite
            }
            return ''
        },
        attestationUrl() {
            if (!this.demande || !this.demande.attestation) {
                return ''
            }
            // "public/attestation/xxx.pdf" est servi via le lien symbolique à "/storage/attestation/xxx.pdf"
            return '/' + this.demande.attestation.replace(/^public\//, 'storage/')
        },
        attestationIsImage() {
            return /\.(png|jpe?g)$/i.test(this.attestationUrl)
        }
    },
    methods: {
        async fetchIndivDemande() {
            this.isLoading = true
            try {
                const res = await getOneIndivDemande(this.alumniId)

                if (res && typeof res === 'object') {
                    this.demande = res;
                } else {
                    this.$swal.fire({ title: 'Erreur', text: 'Problemme lors de la recupération des données', icon: 'error' })
                }
            } catch (err) {
                this.$swal.fire({ title: 'Erreur', text: err, icon: 'error' })
            } finally {
                this.isLoading = false
            }
        },

        changeStep(to) {
            switch (to) {
                case 2:
                    if (to > this.$stepDemandeIndiv(this.demande.step) && this.$stepDemandeIndiv(this.demande.step) === 1) {
                        this.step2go = 'printed';
                        this.showActionDialog = true
                    }
                    break;
                case 3:
                    if (to > this.$stepDemandeIndiv(this.demande.step) && this.$stepDemandeIndiv(this.demande.step) === 2) {
                        this.step2go = 'delivered';
                        this.showActionDialog = true
                    }
                    break;
                case 0:
                    if (this.$stepDemandeIndiv(this.demande.step) === 1) {
                        this.step2go = 'rejected';
                        this.showActionDialog = true
                    }
                    break;
            }
        },

        async done() {
            this.cancel()
            this.$swal.fire({title: 'Succès', text: 'Action réussie', icon: 'success'})
            await this.fetchIndivDemande()
        },
        cancel() {
            this.showActionDialog = false
            this.step2go = ''
        }
    }
}
</script>

<style scoped>
.admin-page {
    max-width: 100%;
}
.page-header {
    align-items: center;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin-bottom: 12px;
}
.page-kicker {
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 4px;
    text-transform: uppercase;
}
.page-title {
    color: #0f172a;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: 0;
    line-height: 1.2;
    margin: 0;
}
.theme--dark .page-title {
    color: #f8fafc;
}
.page-subtitle {
    color: #64748b;
    font-size: 0.9rem;
    margin: 4px 0 0;
}
.action-button {
    border-radius: 8px;
    box-shadow: none;
    height: 36px;
    text-transform: none;
}
.section-card {
    border-radius: 12px;
}
.profile-header {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    padding: 16px;
}
.profile-initials {
    color: #4f46e5;
    font-size: 1.4rem;
    font-weight: 800;
    text-transform: uppercase;
}
.profile-name {
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.3;
    margin: 0;
}
.profile-birth {
    color: #64748b;
    font-size: 0.9rem;
    margin: 4px 0 0;
}
.profile-dates {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
}
.section-card-header {
    align-items: center;
    display: flex;
    gap: 16px;
    padding: 12px 16px;
}
.section-title {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
}
.section-card-body {
    padding: 16px;
}
.info-label {
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    margin: 0 0 2px;
    text-transform: uppercase;
}
.info-value {
    font-size: 0.95rem;
    font-weight: 500;
    margin: 0;
}
.attestation-frame {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}
@media (max-width: 900px) {
    .page-header {
        align-items: stretch;
        flex-direction: column;
    }
    .profile-header {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
